import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { FullPageSpinner } from '../../components/FullPageSpinner.js';
import { decodeJwtClaims, isClinicAdminFromToken } from './jwt-claims.js';
import { useSession } from './SessionProvider.js';

/**
 * Guarda de rotas restritas a **admin da clínica** (ou superadmin). Precisa
 * ser montada DENTRO de `<RequireAuth>`. Checa o JWT localmente — a
 * autorização real acontece no banco via policies e RPC `is_clinic_admin`.
 *
 * Uso:
 * ```tsx
 * <Route element={<RequireAuth />}>
 *   <Route element={<RequireClinicAdmin />}>
 *     <Route path="/settings/ai" element={<ClinicAiSettingsPage />} />
 *   </Route>
 * </Route>
 * ```
 */
export function RequireClinicAdmin() {
  const { loading, session } = useSession();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (!isClinicAdminFromToken(session.access_token)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

/** Hook utilitário para esconder/mostrar UI condicional — não substitui
 * o guard. Para clínica específica, o banco decide via RLS. */
export function useIsClinicAdmin(): boolean {
  const { session } = useSession();
  return isClinicAdminFromToken(session?.access_token);
}

/** Retorna o `current_clinic_id` do JWT (ou null). */
export function useCurrentClinicId(): string | null {
  const { session } = useSession();
  const claims = decodeJwtClaims(session?.access_token);
  return claims?.current_clinic_id ?? null;
}
